export type LaboratoryTest = {
  id: number;
  name: string;
  display_name?: string;
  [key: string]: unknown;
};

/**
 * Normalizza i test ricevuti da PrimoUp senza perdere slug o altri metadati.
 * `name` resta sempre il nome originale usato dall'integrazione;
 * `display_name` è soltanto l'etichetta locale mostrata in PrimoFastLab.
 */
export function parseLaboratoryTests(
  raw: unknown,
  options: { preserveDisplayNameWhitespace?: boolean } = {}
): LaboratoryTest[] {
  let parsed: unknown = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(parsed)) return [];

  return parsed
    .map((value) => {
      if (!value || typeof value !== "object") return null;
      const source = value as Record<string, unknown>;
      const id = Number(source.id);
      const name = typeof source.name === "string" ? source.name.trim() : "";
      if (!Number.isFinite(id) || !name) return null;

      const rawLocalName =
        typeof source.display_name === "string"
          ? source.display_name
          : "";
      const localName = options.preserveDisplayNameWhitespace
        ? rawLocalName
        : rawLocalName.trim();
      const normalized: LaboratoryTest = { ...source, id, name };
      if (
        localName &&
        (options.preserveDisplayNameWhitespace || localName !== name)
      ) {
        normalized.display_name = localName;
      } else {
        delete normalized.display_name;
      }
      return normalized;
    })
    .filter((value): value is LaboratoryTest => value !== null);
}

export function laboratoryTestLabel(
  test: Pick<LaboratoryTest, "name" | "display_name">
): string {
  return test.display_name?.trim() || test.name;
}

export function laboratoryTestLabelByOriginalName(
  raw: unknown,
  originalName: string
): string {
  const normalizedName = originalName.trim().toLocaleLowerCase("it-IT");
  const match = parseLaboratoryTests(raw).find(
    (test) => test.name.toLocaleLowerCase("it-IT") === normalizedName
  );
  return match ? laboratoryTestLabel(match) : originalName;
}
